import React, { useState, useEffect } from 'react';

/* ── same responsive hook as Home ── */
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

const contactMeta = [
  { icon: '✉', label: 'Email',         value: 'appsvitlimited@gmail.com' },
  { icon: '📍', label: 'Location',      value: 'Bengaluru, India'    },
  { icon: '⏱', label: 'Response Time', value: 'Within 24 hours'   },
];

const Contact = () => {
  /* ── same breakpoints & px variable as Home ── */
  const w        = useWindowWidth();
  const isMobile = w <= 600;
  const isTablet = w <= 900;
  const px       = isMobile ? '18px' : isTablet ? '28px' : '40px';

  const [formData,  setFormData]  = useState({ name: '', email: '', message: '', companyWebsite: '' });
  const [submitted, setSubmitted] = useState(false);
  const [focused,   setFocused]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errors = {};
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    if (name.length < 2) errors.name = 'Please enter at least 2 characters.';
    if (name.length > 100) errors.name = 'Name must not exceed 100 characters.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Please enter a valid email address.';
    if (email.length > 254) errors.email = 'Email must not exceed 254 characters.';
    if (message.length < 10) errors.message = 'Please enter at least 10 characters.';
    if (message.length > 2000) errors.message = 'Message must not exceed 2000 characters.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      return;
    }
    setLoading(true);
    setError(null);
    setFieldErrors({});
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const res = await fetch(`${apiUrl}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        if (payload?.errors) setFieldErrors(payload.errors);
        throw new Error(payload?.message || 'We could not send your message. Please try again.');
      }
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '', companyWebsite: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.name === 'AbortError'
        ? 'The request timed out. Please check your connection and try again.'
        : (err.message || 'Something went wrong. Please try again.'));
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    padding: '12px 16px',
    borderRadius: 10,
    border: `1px solid ${focused === field ? 'rgba(45,212,191,0.55)' : 'rgba(255,255,255,0.08)'}`,
    fontSize: 15,
    outline: 'none',
    fontFamily: "'DM Sans',sans-serif",
    color: '#f0f4f8',
    background: focused === field ? 'rgba(45,212,191,0.04)' : 'rgba(255,255,255,0.03)',
    boxShadow: focused === field ? '0 0 0 3px rgba(45,212,191,0.1)' : 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
    caretColor: '#2dd4bf',
    width: '100%',
    boxSizing: 'border-box',
  });

  return (
    /* ── outer wrapper: same pattern as Home ── */
    <main
      className="page-enter"
      style={{ padding: '0 0 80px', maxWidth: 1160, margin: '0 auto', width: '100%' }}
    >
      <section style={{
        padding: isMobile ? `56px ${px} 40px` : `80px ${px} 60px`,
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr',
        gap: isMobile ? '40px' : '60px',
        alignItems: 'center',
      }}>

        {/* ── Left / Info panel ── */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="section-label">Get in Touch</div>

          <h1 style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: isMobile ? '30px' : 'clamp(28px,3vw,42px)',
            fontWeight: 700, lineHeight: 1.15,
            color: '#f0f4f8', letterSpacing: '-0.02em', margin: 0,
          }}>
            Let's build something together
          </h1>

          <p style={{ fontSize: isMobile ? '15px' : '16px', lineHeight: 1.75, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
            Whether you have a project in mind, a question, or just want to say hello — we'd love to hear from you.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
            {contactMeta.map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(45,212,191,0.1)', border: '1px solid rgba(45,212,191,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#4a5568' }}>{item.label}</div>
                  <div style={{ fontSize: 14, color: '#8b9bb4', marginTop: 2 }}>{item.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 3, width: 80, borderRadius: 3, background: 'linear-gradient(90deg,#2dd4bf,#818cf8)', marginTop: 8 }} />
        </aside>

        {/* ── Form card ── */}
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          padding: isMobile ? '28px 20px' : '40px 36px',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}>
          {submitted ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '40px 20px', textAlign: 'center', minHeight: 280 }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(45,212,191,0.12)', border: '1px solid rgba(45,212,191,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: '#2dd4bf' }}>✓</div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, color: '#f0f4f8', margin: 0 }}>Message Sent!</h2>
              <p style={{ fontSize: 15, color: '#8b9bb4', lineHeight: 1.7, fontWeight: 300, maxWidth: 300, margin: 0 }}>
                Thank you for reaching out. We'll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 600, color: '#f0f4f8', marginBottom: 4, letterSpacing: '-0.01em' }}>Send a Message</h2>
              <p style={{ fontSize: 13, color: '#4a5568', marginBottom: 28 }}>All fields are required.</p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                  <label htmlFor="companyWebsite">Company website</label>
                  <input id="companyWebsite" name="companyWebsite" type="text" value={formData.companyWebsite} onChange={handleChange} tabIndex="-1" autoComplete="off" />
                </div>
                {[
                  { id: 'name',  type: 'text',  label: 'Your Name',     placeholder: 'Jane Smith'       },
                  { id: 'email', type: 'email', label: 'Email Address', placeholder: 'jane@company.com' },
                ].map((field) => (
                  <div key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label htmlFor={field.id} style={{ fontSize: 13, fontWeight: 500, color: '#8b9bb4' }}>{field.label}</label>
                    <input
                      id={field.id} type={field.type} name={field.id}
                      value={formData[field.id]} onChange={handleChange}
                      onFocus={() => setFocused(field.id)} onBlur={() => setFocused(null)}
                      placeholder={field.placeholder} style={inputStyle(field.id)} required
                      minLength={field.id === 'name' ? 2 : undefined}
                      maxLength={field.id === 'name' ? 100 : 254}
                      aria-invalid={Boolean(fieldErrors[field.id])}
                      aria-describedby={`${field.id}-error`}
                    />
                    {fieldErrors[field.id] && <span id={`${field.id}-error`} style={{ fontSize: 12, color: '#f87171' }}>{fieldErrors[field.id]}</span>}
                  </div>
                ))}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label htmlFor="message" style={{ fontSize: 13, fontWeight: 500, color: '#8b9bb4' }}>Message</label>
                  <textarea
                    id="message" name="message"
                    value={formData.message} onChange={handleChange}
                    onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
                    placeholder="Tell us about your project or question..."
                    style={{ ...inputStyle('message'), minHeight: 130, resize: 'vertical', lineHeight: 1.6 }}
                    required
                    minLength={10} maxLength={2000}
                    aria-invalid={Boolean(fieldErrors.message)} aria-describedby="message-error"
                  />
                  {fieldErrors.message && <span id="message-error" style={{ fontSize: 12, color: '#f87171' }}>{fieldErrors.message}</span>}
                </div>

                {error && (
                  <p role="alert" style={{ fontSize: 13, color: '#f87171', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '10px 14px', margin: 0 }}>
                    {error}
                  </p>
                )}

                <button type="submit" disabled={loading} style={{
                  padding: '13px 28px', fontSize: 15,
                  fontFamily: "'DM Sans',sans-serif", fontWeight: 600,
                  background: loading ? 'rgba(45,212,191,0.4)' : 'linear-gradient(135deg,#2dd4bf,#818cf8)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, boxShadow: '0 4px 24px rgba(45,212,191,0.2)',
                  marginTop: 4, width: '100%', transition: 'background 0.2s',
                }}>
                  {loading ? 'Sending…' : (<>Send Message <span style={{ fontSize: 18, lineHeight: 1 }}>→</span></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default Contact;
